import { NavLink } from "react-router-dom";
import { useState } from "react";
import { X, Menu, LayoutDashboard, FileText, Heart, BellRing, Bell, Bookmark, User as UserIcon, Sparkles, LogOut, Crown } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Logo } from "../Logo";
import { useAuth } from "../../context/AuthContext";

const NAV_ITEMS = [
  { id: "overview", label: "Resumo", icon: LayoutDashboard },
  { id: "applications", label: "Candidaturas", icon: FileText },
  { id: "recommendations", label: "Recomendado", icon: Sparkles },
  { id: "saved", label: "Favoritos", icon: Heart },
  { id: "searches", label: "Pesquisas guardadas", icon: Bookmark },
  { id: "alerts", label: "Alertas", icon: BellRing },
  { id: "notifications", label: "Notificações", icon: Bell },
  { id: "profile", label: "Perfil", icon: UserIcon },
];

export const CANDIDATE_NAV_ITEMS = NAV_ITEMS;

export function CandidateSidebar({ activeTab, onChange, counts = {}, isPremium, onLogout }) {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initial = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const handleChange = (id) => {
    onChange(id);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile trigger */}
      <div className="lg:hidden sticky top-16 z-30 px-4 sm:px-8 py-3 bg-white/95 backdrop-blur border-b border-black/5 flex items-center justify-between">
        <Button
          data-testid="candidate-sidebar-toggle"
          variant="outline" size="sm" className="rounded-full"
          onClick={() => setMobileOpen(true)}>
          <Menu className="w-4 h-4 mr-1.5" /> Menu
        </Button>
        <p className="text-sm font-semibold text-slate-700 capitalize">
          {NAV_ITEMS.find((i) => i.id === activeTab)?.label || "Painel"}
        </p>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/50"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="candidate-sidebar"
        className={`fixed lg:sticky top-0 lg:top-16 left-0 z-50 lg:z-10 h-screen lg:h-[calc(100vh-4rem)] w-72 lg:w-64 bg-white border-r border-black/5 flex flex-col transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}>
        {/* Mobile close */}
        <div className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-black/5">
          <Logo />
          <button onClick={() => setMobileOpen(false)} className="p-2 rounded-full hover:bg-slate-100" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Header: avatar + nome */}
        <div className="px-5 py-6 border-b border-black/5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 font-black flex items-center justify-center text-lg shrink-0">
              {initial}
            </div>
            <div className="min-w-0">
              <p className="font-heading font-bold tracking-tight text-sm text-slate-900 truncate">{user?.name || "Candidato"}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          {isPremium && (
            <Badge className="mt-3 bg-amber-400 text-slate-950 hover:bg-amber-400">
              <Crown className="w-3 h-3 mr-1" /> Premium IA
            </Badge>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            const badge = counts[item.id];
            return (
              <button
                key={item.id}
                data-testid={`sidebar-${item.id}`}
                onClick={() => handleChange(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  active
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
                {badge != null && badge > 0 && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${active ? "bg-amber-400 text-slate-900" : "bg-amber-100 text-amber-700"}`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-3 pb-5 pt-3 border-t border-black/5 space-y-1">
          <NavLink to="/empregos" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">
            <Sparkles className="w-4 h-4" />
            <span>Explorar vagas</span>
          </NavLink>
          {onLogout && (
            <button
              data-testid="sidebar-logout"
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
